import React from 'react';
import proptypes from 'prop-types';
import {
  arrayOf as arrayOfType,
  customType,
  params,
  namedFragment,
  shape,
  typename,
  bool as boolType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { HeroFlattenImageComponent } from './HeroFlattenImageComponent';
import { normalizeHeroFlattenImageData } from '../utils/normalizeHeroFlattenImageData';

const {
  string,
  number,
  bool,
  shape: propShape,
  node
} = proptypes;

const ImpressionProviderWrapper = ({
  children = null,
  shouldWrapWithImpressionProvider = false,
  entryId = null
}) => {
  if (shouldWrapWithImpressionProvider) {
    return (
      <ImpressionProvider
        data={{
          id: entryId || '',
          name: 'HeroFlattenImage',
          component: 'HeroFlattenImage',
          type: 'content',
        }}
      >
        {children}
      </ImpressionProvider>
    );
  }
  return children;
};
ImpressionProviderWrapper.propTypes = {
  children: node,
  shouldWrapWithImpressionProvider: bool,
  entryId: string
};

const HeroFlattenImage = ({
  componentId = '',
  layoutRendererPrerenderedChildIndex = undefined,
  isB2C = false,
  proCustomerSegment = undefined,
  livePreviewData = null,
  ssr = true,
  slotContentId: slotName = null,
  mediaCollectionIndex = 0,
  // Needed to bypass HeroCarousel ImpressionProvider
  shouldWrapWithImpressionProvider = false
}) => {
  const { data: componentData } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass: 'HeroFlattenImage',
    },
    ssr,
    skip: !!slotName || !!livePreviewData,
  });

  const { data: slotData } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: { id: slotName },
    skip: !slotName
  });

  const normalizedData = normalizeHeroFlattenImageData({
    livePreviewData,
    componentData,
    slotData,
    mediaCollectionIndex
  });

  const heroFlattenImageData = normalizedData?.heroData;

  // If we have livePreviewData, render immediately, ignoring loading/error
  if (!heroFlattenImageData || heroFlattenImageData.length === 0) {
    return null;
  }

  return (
    <div data-component="HeroFlattenImage">
      <ImpressionProviderWrapper
        shouldWrapWithImpressionProvider={shouldWrapWithImpressionProvider}
        entryId={componentId || normalizedData?.entryId}
      >
        <div
          // eslint-disable-next-line
          className={`sui-grid sui-grid-cols-1 sui-gap-4 md:sui-gap-6 xl:sui-gap-8 ${heroFlattenImageData?.length > 1 ? "md:sui-grid-cols-4" : ""}`}
          data-testid="hero-flatten-image-container"
        >
          {heroFlattenImageData.map((media, mediaIndex) => {
            const {
              id,
              altText,
              link,
              mobilePreviewImage,
              previewImage,
              proAnalyticsCampaign,
              proAnalyticsComponent,
              title,
              shouldLoopVideo,
              sourceData
            } = media;
            return (
              <React.Fragment key={mediaIndex}>
                <HeroFlattenImageComponent
                  layoutRendererPrerenderedChildIndex={layoutRendererPrerenderedChildIndex || mediaIndex}
                  isB2C={isB2C}
                  proCustomerSegment={proCustomerSegment}
                  id={id}
                  altText={altText}
                  link={link}
                  mobilePreviewImage={mobilePreviewImage}
                  previewImage={previewImage}
                  proAnalyticsCampaign={proAnalyticsCampaign}
                  proAnalyticsComponent={proAnalyticsComponent}
                  title={title}
                  shouldLoopVideo={shouldLoopVideo}
                  sourceData={sourceData}
                />
              </React.Fragment>
            );
          })}
        </div>
      </ImpressionProviderWrapper>
    </div>
  );
};

HeroFlattenImage.propTypes = {
  componentId: string,
  layoutRendererPrerenderedChildIndex: number,
  isB2C: bool,
  proCustomerSegment: string,
  livePreviewData: propShape(HeroFlattenImageComponent.propTypes),
  ssr: bool,
  slotContentId: string,
  mediaCollectionIndex: number,
  shouldWrapWithImpressionProvider: bool
};

HeroFlattenImage.displayName = 'HeroFlattenImage';

// Do not touch
const DamMediaFragment = namedFragment({
  inline: false,
  fragmentType: 'DamMedia',
  fragmentAlias: 'DamMediaV3'
}).shape({
  damContentSelector: shape({
    assetData: arrayOfType(
      shape({
        selectedImageUrl: stringType()
      })
    )
  }),
  damDownloadedContent: shape({
    url: stringType()
  }),
  imageHotspot: shape({
    id: stringType(),
    title: stringType(),
    imageUrl: stringType(),
    hotspots: stringType(),
    hotspotActions: arrayOfType(shape({
      id: stringType(),
      url: stringType(),
      title: stringType(),
      drawer: shape({
        headline: stringType(),
      }),
    })),
    __typename: typename('ImageHotspot')
  }),
  __typename: typename('DamMedia')
});

HeroFlattenImage.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['HeroFlattenImage']).isRequired()
  }).shape({
    HeroFlattenImage: namedFragment({
      inline: true,
      fragmentType: 'HeroFlattenImage',
      fragmentAlias: 'HeroFlattenImage'
    }).shape({
      id: stringType(),
      title: stringType(),
      previewImage: DamMediaFragment,
      mobilePreviewImage: DamMediaFragment,
      posterImage: DamMediaFragment,
      desktopPosterImage: DamMediaFragment,
      link: stringType(),
      altText: stringType(),
      componentName: stringType(),
      shouldLoopVideo: boolType(),
      proAnalyticsCampaign: stringType(),
      proAnalyticsComponent: stringType(),
      __typename: typename('HeroFlattenImage')
    })
  }),
  slot: params({
    id: stringType(),
  }).shape({
    id: stringType(),
    entryId: stringType(),
    mediaCollections: arrayOfType(shape({
      entryId: stringType(),
      media: arrayOfType(shape({
        entryId: stringType(),
        title: stringType(),
        isAutoplay: boolType(),
        sourceDesktop: arrayOfType(shape({
          entryId: stringType(),
          hyperlink: stringType(),
          altText: stringType(),
          uri: stringType(),
          hotspot: stringType()
        })),
        sourceMobile: arrayOfType(shape({
          entryId: stringType(),
          hyperlink: stringType(),
          altText: stringType(),
          uri: stringType(),
          hotspot: stringType()
        })),
      })),
    })),
    __typename: typename('newSlot')
  })
};

export { HeroFlattenImage };